import type { ElementType, ReactNode } from 'react';
import { Card } from '@/components/ui/card';

interface StepCardProps {
  children?: ReactNode;
  Icon: ElementType;
  title: string;
  description: string;
  headerAction?: ReactNode;
  /** Header→body gap. Figma varies per step: space-y-6 (Rebalancing), space-y-4 (Universe). */
  bodySpacing?: string;
}

export function StepCard({
  children,
  Icon,
  title,
  description,
  headerAction,
  bodySpacing = 'space-y-6',
}: StepCardProps) {
  // Outer step card matches Figma's top-level pattern for every step
  // (RebalancingSchedule.tsx l.425-426): <Card className="p-6"> wrapping a
  // single <div className="space-y-6"> that holds the header row + the step
  // body. The single wrapper is load-bearing: our ui/Card is
  // `flex flex-col gap-6`, so putting the header and body as SEPARATE card
  // children would add the 24px flex gap ON TOP of any header margin
  // (double spacing). One wrapper → the flex gap is inert and space-y-6
  // alone controls the 24px header→body gap.
  // Icon container: w-10 h-10 rounded-lg (NOT rounded-full).
  // Title:          font-medium text-[20px] (NOT text-lg font-semibold).
  // Description:    text-muted-foreground text-[15px].
  return (
    <Card className="p-6 bg-card">
      <div className={bodySpacing}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-[20px]">{title}</h3>
            <p className="text-muted-foreground text-[15px]">{description}</p>
          </div>
          {headerAction}
        </div>
        {children}
      </div>
    </Card>
  );
}
